<template>
  <!-- Dialog 1: Select Platforms -->
  <v-dialog v-model="step1" max-width="640" persistent scrollable>
    <v-card class="pa-4 position-relative">
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="#107569"
        height="3"
        absolute
        top
      ></v-progress-linear>

      <v-card-title class="d-flex justify-space-between align-center">
        <div class="text-h6 font-weight-bold">
          Select Platforms and Accounts
        </div>
      </v-card-title>

      <v-card-subtitle class="text-caption mb-3">
        Your campaign will be published across all selected platforms.
        <br />
        Choose one account per platform.
      </v-card-subtitle>

      <v-divider class="mb-2"></v-divider>

      <v-card-text>
        <v-expansion-panels v-model="activePanels" multiple>
          <v-expansion-panel
            v-for="(platform) in platforms"
            :key="platform.name"
          >
            <v-expansion-panel-title>
              <div class="d-flex align-center">
                <v-avatar size="24" class="mr-2" v-if="platform.icon">
                  <v-img :src="platform.icon" />
                </v-avatar>
                <span class="text-subtitle-2 font-weight-medium">
                  {{ platform.label }}
                </span>
              </div>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <div class="as">
                <v-radio-group
                  v-model="selectedAccounts[platform.name]"
                  density="compact"
                  hide-details
                >
                  <v-radio
                    v-for="account in platform.accounts"
                    :key="account.id"
                    :value="{
                      id: account.id,
                      name: account.name,
                      avatar: account.avatar,
                    }"
                    :color="'#107569'"
                    class="my-2"
                  >
                    <template #label>
                      <div class="d-flex align-center">
                        <v-avatar size="36" class="mx-2">
                          <v-img :src="account.avatar" />
                        </v-avatar>
                        <span>{{ account.name }}</span>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-divider class="my-2"></v-divider>

      <v-card-actions class="d-flex justify-end">
        <v-btn
          v-if="hasSelectedAccounts"
          color="#F2F2F2"
          style="color: #4d4d4d"
          class="con"
          variant="flat"
          @click="clearaccount"
        >
          Clear
        </v-btn>
        <v-btn
          color="#237276"
          class="con"
          variant="flat"
          @click="goToStep2"
          :disabled="!hasSelectedAccounts"
        >
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Dialog 2: Choose Objective -->
  <v-dialog v-model="step2" max-width="700" persistent>
    <v-card class="pa-6">
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="text-h6 font-weight-bold">Choose Objective</div>
      </v-card-title>

      <v-card-text class="d-flex">
        <v-radio-group v-model="selectedObjective" class="mr-6">
          <v-radio
            v-for="item in objectives"
            :key="item.value"
            :value="item.value"
            :color="'#107569'"
            class="mb-4"
          >
            <template #label>
              <div class="d-flex align-center ga-4">
                <v-avatar size="40">
                  <v-img :src="item.icon" />
                </v-avatar>
                <span>{{ item.label }}</span>
              </div>
            </template>
          </v-radio>
        </v-radio-group>

        <div v-if="currentObjective" style="max-width: 350px">
          <v-img :src="currentObjective.image" width="90" class="mb-4" />
          <h4 class="text-subtitle-1 font-weight-bold">
            {{ currentObjective.label }}
          </h4>
          <p class="text-body-2 mb-2">{{ currentObjective.desc }}</p>
          <div class="d-flex flex-wrap ga-2 mt-1">
            <p class="text-subtitle-1 font-weight-bold mb-1">Good for</p>
            <div class="d-flex flex-wrap ga-2 mt-1">
              <v-chip
                v-for="tag in currentObjective.tags"
                :key="tag"
                color="grey-lighten-4"
                class="text-body-2 font-weight-medium px-4 py-2"
                variant="flat"
                size="default"
                label
              >
                {{ tag }}
              </v-chip>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="d-flex justify-end mt-4">
        <v-btn color="#237276" class="con" variant="flat" @click="finalSubmit">
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import apiClient from "@/core/buildRequest";
import { useSuperAddStore } from "@/store/superAddStore";
import {
  clearModuleCache,
  clearModuleSelection,
  createCacheKey,
  getModuleCache,
  getModuleSelection,
  setModuleCache,
  setModuleSelection,
} from "@/core/moduleCache";

const SELECTION_MODULE = "super-ad";
const ACCOUNTS_CACHE_MODULE = "super-ad-accounts";
const ACCOUNTS_CACHE_KEY = createCacheKey({ endpoint: "platforms-account" });

function normalizePlatformAccounts(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}
export default {
  name: "PlatformSelector",
  data() {
    return {
      step1: false,
      step2: false,
      loading: true,
      selectedAccounts: {},
      platforms: [],
      store: useSuperAddStore(),
      activePanels: [0],
      selectedObjective: "awareness",
      objectives: [
        {
          value: "awareness",
          label: "Awareness",
          desc: "Show your ads to people who are most likely to remember them.",
          icon: require("@/components/SuperApp/DialogImg/Awar.svg"),
          image: require("@/components/SuperApp/DialogImg/right-awar.svg"),
          tags: [
            "Reach",
            "Brand awareness",
            "Video views",
            "Store location awareness",
            "Place Promotion",
            "Ad Engagement",
          ],
        },
        {
          value: "leads",
          label: "Leads",
          desc: "Drive more leads by encouraging people to submit forms.",
          icon: require("@/components/SuperApp/DialogImg/leads.svg"),
          image: require("@/components/SuperApp/DialogImg/leads.svg"),
          tags: ["Form submission", "Lead generation", "Calls"],
        },
        {
          value: "video_views",
          label: "Video views",
          desc: "Optimize delivery to people likely to watch your video.",
          icon: require("@/components/SuperApp/DialogImg/video.svg"),
          image: require("@/components/SuperApp/DialogImg/video.svg"),
          tags: ["Video awareness", "Retention", "Story views"],
        },
      ],
    };
  },
  computed: {
    currentObjective() {
      return this.objectives.find((objective) => objective.value === this.selectedObjective);
    },
    hasSelectedAccounts() {
      return Object.values(this.selectedAccounts).some((account) => !!account);
    },
  },
  async mounted() {
    await this.initializeSelection();
  },
  methods: {
    async initializeSelection() {
      const loaded = await this.fetchPlatforms();
      if (!loaded) {
        this.step1 = true;
        return;
      }

      const savedAccounts = this.getSavedAccounts();
      const savedObjective = this.getSavedObjective();
      const selectedAccounts = this.getValidSavedAccounts(savedAccounts);
      const hasSavedSelection = Object.values(selectedAccounts).some(
        (account) => !!account
      );
      const hasValidObjective = this.objectives.some(
        (objective) => objective.value === savedObjective
      );

      this.selectedAccounts = selectedAccounts;
      if (hasSavedSelection && hasValidObjective) {
        this.selectedObjective = savedObjective;
        // Persist the sanitized result so removed accounts are not reused later.
        this.applyConfirmedSelection(selectedAccounts, savedObjective, true);
        this.step1 = false;
        return;
      }

      if (Object.keys(savedAccounts).length) {
        clearModuleSelection(SELECTION_MODULE);
        localStorage.removeItem("superselected_accounts");
        localStorage.removeItem("superselected_objective");
        localStorage.removeItem("super_selected_platforms");
      }

      this.step1 = true;
    },
    async fetchPlatforms() {
      this.loading = true;
      const cached = getModuleCache(ACCOUNTS_CACHE_MODULE, ACCOUNTS_CACHE_KEY);
      if (Array.isArray(cached?.data)) {
        this.applyPlatformAccounts(cached.data);
        this.loading = false;
      }
      try {
        const response = await apiClient.get("/super-ad/platforms-account");
        const accounts = normalizePlatformAccounts(response.data?.data);
        if (!Array.isArray(accounts) || !accounts.length) {
          // Drop a stale empty/partial cache so a later OAuth sync is visible.
          if (!accounts.length) clearModuleCache(ACCOUNTS_CACHE_MODULE);
          return this.platforms.length > 0;
        }
        this.applyPlatformAccounts(accounts);
        setModuleCache(ACCOUNTS_CACHE_MODULE, ACCOUNTS_CACHE_KEY, accounts);
        return true;
      } catch (error) {
        console.error("❌ Error fetching accounts:", error);
        return this.platforms.length > 0;
      } finally {
        this.loading = false;
      }
    },
    applyPlatformAccounts(accounts) {
        const platformsMap = {};
        const platformLabels = {
          facebook: "Meta",
          snapchat: "Snapchat account",
          tiktok: "Tik Tok account",
        };

        for (const acc of accounts) {
          if (!acc?.provider) continue;
          const provider = String(acc.provider).toLowerCase();
          if (!platformsMap[provider]) {
            platformsMap[provider] = {
              name: provider,
              label: platformLabels[provider] || provider,
              icon: this.getPlatformIcon(provider),
              accounts: [],
            };
          }

          platformsMap[provider].accounts.push({
            id: acc.id,
            name: acc.account_name,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(
              acc.account_name || "account"
            )}&background=random`,
          });
        }

        this.platforms = Object.values(platformsMap);
    },
    getSavedAccounts() {
      try {
        const scopedSelection = getModuleSelection(SELECTION_MODULE, null);
        const parsedAccounts = scopedSelection?.accounts || {};
        return parsedAccounts && typeof parsedAccounts === "object" && !Array.isArray(parsedAccounts)
          ? parsedAccounts
          : {};
      } catch (error) {
        console.warn("Invalid saved Super Ad accounts", error);
        return {};
      }
    },
    getSavedObjective() {
      return getModuleSelection(SELECTION_MODULE, null)?.objective;
    },
    getValidSavedAccounts(savedAccounts) {
      return this.platforms.reduce((selection, platform) => {
        const savedAccount = savedAccounts[platform.name];
        const availableAccount = platform.accounts.find(
          (account) => account.id === savedAccount?.id
        );

        selection[platform.name] = availableAccount || null;
        return selection;
      }, {});
    },
    getPlatformIcon(provider) {
      switch (provider) {
        case "facebook":
          return require("@/assets/images/icons/meta.png");
        case "snapchat":
          return require("@/assets/images/icons/snap.png");
        case "tiktok":
          return require("@/assets/images/icons/tiktok.png");
        default:
          return "";
      }
    },
    capitalize(word) {
      if (!word) return "";
      return word.charAt(0).toUpperCase() + word.slice(1);
    },
    goToStep2() {
      const hasSelected = Object.values(this.selectedAccounts).some(
        (account) => !!account
      );

      if (!hasSelected) {
        this.$toast?.warning?.("Please select at least one account.");
        return;
      }

      this.step1 = false;
      this.step2 = true;
    },
    finalSubmit() {
      this.step2 = false;
      this.applyConfirmedSelection(
        this.selectedAccounts,
        this.selectedObjective,
        true
      );
    },
    applyConfirmedSelection(accounts, objective, persist = false) {
      const selectedPlatforms = this.buildSelectedPlatforms(accounts);

      this.store.setSelectedAccounts({ ...accounts });
      this.store.platforms = selectedPlatforms;
      this.store.campaignObjective = objective;
      this.store.setSelectedObjective(objective);

      if (persist) {
        setModuleSelection(SELECTION_MODULE, { accounts, objective });
        localStorage.setItem("superselected_accounts", JSON.stringify(accounts));
        localStorage.setItem("superselected_objective", objective);
        localStorage.setItem(
          "super_selected_platforms",
          JSON.stringify(selectedPlatforms)
        );
      }
    },
    buildSelectedPlatforms(accounts) {
      const currencyMap = { facebook: "USD", snapchat: "USD", tiktok: "USD" };
      const iconMap = {
        facebook: new URL("@/assets/images/icons/meta.png", import.meta.url).href,
        snapchat: new URL("@/assets/images/icons/snap.png", import.meta.url).href,
        tiktok: new URL("@/assets/images/icons/tiktok.png", import.meta.url).href,
      };

      return Object.keys(accounts)
        .filter((platformKey) => accounts[platformKey])
        .map((platformKey) => ({
          name: platformKey === "facebook" ? "Meta" : this.capitalize(platformKey),
          icon: iconMap[platformKey],
          budget: "",
          currency: currencyMap[platformKey] || "USD",
          budgetType: "daily",
        }));
    },
    openSelection() {
      this.selectedAccounts = this.getValidSavedAccounts(this.getSavedAccounts());
      this.selectedObjective =
        this.getSavedObjective() || "awareness";
      this.step2 = false;
      this.step1 = true;
    },
    clearaccount() {
      this.platforms.forEach((platform) => {
        this.selectedAccounts[platform.name] = null;
      });
    },
  },
};
</script>

<style scoped>
.v-card-text {
  max-height: 60vh;
  overflow-y: auto;
}

.as {
  padding: 0.8rem;
  background-color: #f2f2f299;
  border-radius: 0.5rem;
  margin: 0 !important;
}

.text-subtitle-2 {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #202224;
}

.con,
.can {
  border-radius: 8px;
  text-transform: capitalize;
  width: 7rem;
}

::v-deep(.v-selection-control-group){
  gap: 1rem !important;
  padding:20px 0 !important;
}
</style>
